import {
  createContext,
  forwardRef,
  useContext,
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react';

import { cn } from '@/lib/utils';

/**
 * Tabela do Design System (ADR-0065), o `.table` do pacote: cabeçalho em caixa
 * alta de 11px com tracking 0.08em sobre um hairline, células separadas por
 * tinta a 8% e linha do corpo com realce de 4% no hover.
 *
 * As classes do pacote **não** viram CSS: `.table` colidiria com utilitário do
 * Tailwind — já quebrou o build uma vez (E37) e há guard próprio
 * (`scripts/tailwind_apply_collision_lint.py`). Aqui vira componente.
 *
 * O hover é do corpo, não do cabeçalho: `TableBody` publica isso por contexto
 * para que `TableRow` não precise ser informada duas vezes.
 */

const CorpoContext = createContext(false);

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(function Table(
  { className, ...props },
  ref,
) {
  return (
    <table ref={ref} className={cn('w-full border-collapse text-[14px]', className)} {...props} />
  );
});

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableHeader({ className, ...props }, ref) {
  return <thead ref={ref} className={cn(className)} {...props} />;
});

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableBody({ className, children, ...props }, ref) {
  return (
    <CorpoContext.Provider value={true}>
      <tbody ref={ref} className={cn(className)} {...props}>
        {children}
      </tbody>
    </CorpoContext.Provider>
  );
});

export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(function TableFooter({ className, ...props }, ref) {
  return <tfoot ref={ref} className={cn('text-fg-muted', className)} {...props} />;
});

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  function TableRow({ className, ...props }, ref) {
    const noCorpo = useContext(CorpoContext);
    return (
      <tr
        ref={ref}
        className={cn(
          noCorpo && 'transition-colors duration-fast ease-sc hover:bg-fg/[0.04]',
          className,
        )}
        {...props}
      />
    );
  },
);

export const TableHead = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  function TableHead({ className, ...props }, ref) {
    return (
      <th
        ref={ref}
        className={cn(
          'border-b border-border p-sc-2 text-left text-[11px] font-medium uppercase tracking-[0.08em] text-fg-dim',
          className,
        )}
        {...props}
      />
    );
  },
);

export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  function TableCell({ className, ...props }, ref) {
    return (
      <td ref={ref} className={cn('border-b border-fg/[0.08] p-sc-2', className)} {...props} />
    );
  },
);

export const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(function TableCaption({ className, ...props }, ref) {
  return (
    <caption
      ref={ref}
      className={cn('pb-sc-2 text-left text-caption text-fg-muted', className)}
      {...props}
    />
  );
});
